import type { ObjectPaymentReportRow, ObjectPaymentsReportFilter, UserType } from '@econtractor/types';
import { DateTime } from 'luxon';
import { getPresetPeriodRange, periodRangeToMillis } from '../../ReportDate/periodUtils';
import type { ReportVatFilter } from '../../types';
import type { ReportDateFilterState } from '../../helper/reportObjectsStorage';
import { formatPrice } from 'utils/helpers/functions/formatPrice';

export type PaymentGroupBy = 'object' | 'owner' | 'author';
export type PaymentSortKey = 'ID' | 'name' | 'owner' | 'author' | 'date' | 'amount' | 'document';
export interface PaymentReportState extends Omit<ObjectPaymentsReportFilter, 'isVat'> {
    isVat: ReportVatFilter;
    selectedObject: { ID: number; objectName: string } | null;
    owner: UserType | null;
    author: UserType | null;
    dateFilter: ReportDateFilterState;
    detail: boolean;
    groupBy: PaymentGroupBy;
}
export interface PaymentReportGroup {
    id: number;
    name: string;
    rows: ObjectPaymentReportRow[];
}
export const paymentColumns = ['name', 'owner', 'author', 'date', 'amount', 'document'] as const;
const storageKey = 'reportObjectPayments.filters';

export const createDefaultPaymentFilters = (): PaymentReportState => {
    const { start, end } = getPresetPeriodRange('month');
    return {
        dateFilter: { period: 'month', dates: periodRangeToMillis(start, end), customRange: null },
        includeObjects: true, includeSales: true, includeServiceSales: true,
        selectedObject: null, owner: null, author: null,
        isVat: null, detail: false, groupBy: 'object',
    };
};

export const loadPaymentFilters = (objectScoped = false): PaymentReportState => {
    const defaults = createDefaultPaymentFilters();
    try {
        const saved = JSON.parse(sessionStorage.getItem(objectScoped ? `${storageKey}.objectCard` : storageKey) || 'null') as Partial<PaymentReportState> | null;
        if (!saved) return defaults;
        const dates = saved.dateFilter?.dates;
        const validDates = Array.isArray(dates) && dates.length === 2 && dates.every((v) => Number.isSafeInteger(v) && v > 0 && v <= 8640000000000000) && dates[0] <= dates[1];
        const period = saved.dateFilter?.period;
        const validPeriod = period === 'month' || period === 'quarter' || period === 'year' || period === 'custom';
        return {
            dateFilter: validDates && validPeriod ? {
                period, dates,
                customRange: period === 'custom' ? dates : null,
            } : defaults.dateFilter,
            selectedObject: !objectScoped && saved.selectedObject?.ID ? saved.selectedObject : null,
            owner: !objectScoped && saved.owner?.ID ? saved.owner : null,
            author: !objectScoped && saved.author?.ID ? saved.author : null,
            includeObjects: !objectScoped && typeof saved.includeObjects === 'boolean' ? saved.includeObjects : true,
            includeSales: !objectScoped && typeof saved.includeSales === 'boolean' ? saved.includeSales : true,
            includeServiceSales: !objectScoped && typeof saved.includeServiceSales === 'boolean' ? saved.includeServiceSales : true,
            isVat: !objectScoped && (saved.isVat === -1 || saved.isVat === 0 || saved.isVat === 1) ? saved.isVat : null,
            detail: !objectScoped && saved.detail === true,
            groupBy: saved.groupBy === 'owner' || saved.groupBy === 'author' ? saved.groupBy : 'object',
        };
    } catch {
        return defaults;
    }
};

export const savePaymentFilters = (state: PaymentReportState, objectScoped = false): void => {
    try { sessionStorage.setItem(objectScoped ? `${storageKey}.objectCard` : storageKey, JSON.stringify(state)); } catch { /* Storage can be unavailable. */ }
};

export const objectPaymentName = (row: ObjectPaymentReportRow): string => row.objectName || row.clientName || `#${row.objectId}`;
export const paymentUserName = (name: string, id: number): string => name || `#${id}`;
export const paymentMoney = (amount: number, usd: boolean): string => `${usd ? '$' : '₴'} ${formatPrice(amount)}`;
export const paymentCurrencyTotals = (rows: ObjectPaymentReportRow[], deleted = false): { uah: number; usd: number } => rows.reduce((totals, row) => {
    if (!!Number(row.documentDeleted) === deleted) {
        totals[Number(row.paymentUsd) ? 'usd' : 'uah'] += Number(row.payment);
    }
    return totals;
}, { uah: 0, usd: 0 });
export const paymentTotals = (rows: ObjectPaymentReportRow[], deleted = false): string => {
    const { uah, usd } = paymentCurrencyTotals(rows, deleted);
    return `${paymentMoney(uah, false)} · ${paymentMoney(usd, true)}`;
};
export const paymentDate = (dt: number): string => DateTime.fromMillis(Number(dt)).toFormat('dd.MM.yyyy HH:mm');

const sortValue = (row: ObjectPaymentReportRow, key: PaymentSortKey): string | number => {
    switch (key) {
        case 'name': return objectPaymentName(row);
        case 'owner': return paymentUserName(row.ownerName, row.ownerId);
        case 'author': return paymentUserName(row.authorName, row.authorId);
        case 'date': return Number(row.dt);
        case 'amount': return Number(row.payment);
        case 'document': return row.documentName || `#${row.generationId}`;
        default: return Number(row.ID);
    }
};
export const sortPayments = (rows: ObjectPaymentReportRow[], key: PaymentSortKey, direction: 'asc' | 'desc', locale: string): ObjectPaymentReportRow[] => [...rows].sort((a, b) => {
    let order = 0;
    if (key === 'amount') order = Number(a.paymentUsd) - Number(b.paymentUsd);
    if (!order) {
        const av = sortValue(a, key);
        const bv = sortValue(b, key);
        order = typeof av === 'number' && typeof bv === 'number' ? av - bv : String(av).localeCompare(String(bv), locale);
    }
    return (direction === 'asc' ? order : -order) || Number(b.ID) - Number(a.ID);
});

export const groupPayments = (rows: ObjectPaymentReportRow[], by: PaymentGroupBy, locale: string): PaymentReportGroup[] => {
    const groups = new Map<number, PaymentReportGroup>();
    rows.forEach((row) => {
        const id = Number(by === 'object' ? row.objectId : by === 'owner' ? row.ownerId : row.authorId);
        const name = by === 'object' ? objectPaymentName(row) : by === 'owner' ? paymentUserName(row.ownerName, id) : paymentUserName(row.authorName, id);
        if (!groups.has(id)) groups.set(id, { id, name, rows: [] });
        groups.get(id)!.rows.push(row);
    });
    return Array.from(groups.values()).sort((a, b) => a.name.localeCompare(b.name, locale) || a.id - b.id).map((group) => ({ ...group, rows: sortPayments(group.rows, 'ID', 'desc', locale) }));
};
